/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from 'react-responsive-modal';
import { withTheme } from 'styled-components';
import { TextInput, Button } from '@01_people/ui-library';
import { withTranslation, useTranslation, Trans } from 'react-i18next';
import * as ActionCreators from '../../../redux/action';
import { validateEmail } from '../../../utils/common';
import AppLoader from '../../app-loader';
import EmailIcon from '../../../assets/svg/2fa-email.svg';
import ChangeEmailProcessSC from './change-email-process.styled';
import Svgicon from '../../svg-icon';

const FIRST_STEP = 1;
export const SECOND_STEP = 2;
export const THIRD_STEP = 3;
const FOURTH_STEP = 4;


const StepOne = ({ errors, handleTextInputAddress, handleTextInputBlur }) => {
  const { t } = useTranslation('pages');

  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.email.modal.1.title')}
      </h4>
      <p className="change-process-modal__description">
        {t('profile.email.modal.1.description')}
      </p>
      <TextInput
        customClass="change-process-modal__input"
        label={t('profile.email.modal.1.label')}
        errors={errors}
        onChange={handleTextInputAddress}
        onBlur={handleTextInputBlur}
      />
    </div>
  );
};

StepOne.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTextInputAddress: PropTypes.func.isRequired,
  handleTextInputBlur: PropTypes.func.isRequired
};

const StepTwo = ({
  currentEmail,
  errors,
  handleTextInputAddress,
  handleTextInputBlur
}) => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.email.modal.2.title')}
      </h4>
      <p className="change-process-modal__description">
        <Trans
          i18nKey="profile.email.modal.2.description"
          ns="pages"
          values={{ currentEmail }}
        />
      </p>
      <TextInput
        customClass="change-process-modal__input"
        label={t('profile.email.modal.2.label', { currentEmail })}
        errors={errors}
        id="old-verification-code"
        onChange={handleTextInputAddress}
        onBlur={handleTextInputBlur}
      />
    </div>
  );
};

StepTwo.propTypes = {
  currentEmail: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTextInputAddress: PropTypes.func.isRequired,
  handleTextInputBlur: PropTypes.func.isRequired
};

const StepThree = ({
  emailAddress,
  errors,
  handleTextInputAddress,
  handleTextInputBlur
}) => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.email.modal.3.title')}
      </h4>
      <p className="change-process-modal__description">
        <Trans
          i18nKey="profile.email.modal.3.description"
          ns="pages"
          values={{ emailAddress }}
        />
      </p>
      <TextInput
        customClass="change-process-modal__input"
        label={t('profile.email.modal.3.label', { emailAddress })}
        errors={errors}
        id="new-verification-code"
        defaultValue=""
        onChange={handleTextInputAddress}
        onBlur={handleTextInputBlur}
      />
    </div>
  );
};

StepThree.propTypes = {
  emailAddress: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTextInputAddress: PropTypes.func.isRequired,
  handleTextInputBlur: PropTypes.func.isRequired
};

const StepFour = () => {
  const { t } = useTranslation('pages');
  return (
    <div className="change-process-modal">
      <h4 className="change-process-modal__heading">
        {t('profile.email.modal.4.title')}
      </h4>
      <p className="change-process-modal__description">
        {t('profile.email.modal.4.description')}
      </p>
    </div>
  );
};

class ChangeEmailProcess extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      modalOpen: false,
      currentStep: null,
      newEmailAddress: {
        valid: false,
        focused: false,
        blurred: false,
        value: null
      },
      verificationCode: {
        valid: false,
        focused: false,
        blurred: false,
        value: null
      }
    };
  }

  /**
   * Opens modal
   */
  handleChangeEmailBtnClick = () => {
    this.setState(state => ({
      ...state,
      modalOpen: true,
      errors: [],
      currentStep: FIRST_STEP,
      newEmailAddress: {
        ...state.newEmailAddress,
        focused: false,
        blurred: false,
        valid: false,
        value: null
      }
    }));
  };

  validateEmail = (value, blurred = false, callback = null) => {
    const { currentEmail, t } = this.props;

    const emailCheckValid = validateEmail(value);
    const emailSameAsOld = currentEmail === value;
    const emailValid = emailCheckValid && !emailSameAsOld;

    const errors = [];
    if (emailSameAsOld) {
      errors.push(t('profile.email.sameEmail'));
    }
    if (!emailValid && blurred) {
      errors.push(t('profile.email.notValidEmail'));
    }
    this.setState(
      state => ({
        ...state,
        errors,
        newEmailAddress: {
          ...state.newEmailAddress,
          valid: emailValid,
          focused: true,
          blurred: false,
          value
        }
      }),
      callback !== null ? callback(errors) : null
    );
  };

  validateVerificationCode = (value, callback = null) => {
    const { t } = this.props;
    const errors = [];
    const verificationCodeNotEmpty = !(!value || value === 0);
    if (!verificationCodeNotEmpty) {
      errors.push(t('errors:wrong_code'));
    }
    this.setState(
      state => ({
        ...state,
        errors,
        newEmailAddress: {
          ...state.newEmailAddress,
          valid: true,
          focused: false,
          blurred: false
        },
        verificationCode: {
          valid: verificationCodeNotEmpty,
          focused: true,
          blurred: false,
          value
        }
      }),
      callback !== null ? callback(errors) : null
    );
  };

  /**
   * Handler called as user types in new email address
   */
  handleTextInputAddress = value => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case FIRST_STEP:
        this.validateEmail(value);
        break;
      case SECOND_STEP:
      case THIRD_STEP:
        this.validateVerificationCode(value);
        break;
      default:
        break;
    }
  };

  handleTextInputBlur = () => {
    const { currentStep, newEmailAddress, verificationCode } = this.state;

    switch (currentStep) {
      case FIRST_STEP:
        this.validateEmail(newEmailAddress.value, true);
        break;
      case SECOND_STEP:
      case THIRD_STEP:
        this.validateVerificationCode(verificationCode.value);
        break;
      default:
        break;
    }
  };

  handleCloseModalClick = () => {
    this.setState(state => ({
      ...state,
      modalOpen: false
    }));
  };

  nextStep = () => {
    const { currentEmail, changeEmail, verifyEmail } = this.props;
    const { currentStep, newEmailAddress, verificationCode } = this.state;

    // call efects when modal moves
    switch (currentStep) {
      case FIRST_STEP:
        changeEmail(newEmailAddress.value, currentStep, (errors, nextStep) =>
          this.setState(state => ({
            ...state,
            errors,
            currentStep: nextStep
          }))
        );
        break;
      case SECOND_STEP:
        verifyEmail(
          currentEmail,
          verificationCode.value,
          currentStep,
          (errors, nextStep) => {
            this.setState(state => ({
              ...state,
              errors,
              currentStep: nextStep
            }));
          }
        );
        break;
      case THIRD_STEP:
        verifyEmail(
          newEmailAddress.value,
          verificationCode.value,
          currentStep,
          (errors, nextStep) => {
            this.setState(state => ({
              ...state,
              errors,
              currentStep: nextStep,
              verificationCode: {
                valid: false,
                focused: false,
                blurred: false,
                value: null
              }
            }));
          }
        );
        break;
      default:
        break;
    }
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      const { currentStep, newEmailAddress, verificationCode } = this.state;

      switch (currentStep) {
        case FIRST_STEP:
          this.validateEmail(newEmailAddress.value, true, errors =>
            errors.length <= 0 ? this.nextStep() : null
          );
          break;
        case SECOND_STEP:
        case THIRD_STEP:
          this.validateVerificationCode(verificationCode.value, errors =>
            errors.length <= 0 ? this.nextStep() : null
          );
          break;
        case FOURTH_STEP:
          this.handleOkButton();
          break;
        default:
          break;
      }
    }
  };

  handleOkButton = () => {
    const { updateUser, user } = this.props;
    const { newEmailAddress } = this.state;

    updateUser({
      ...user,
      email: newEmailAddress.value
    });
    this.handleCloseModalClick();
  };

  renderModalContent = () => {
    const { currentEmail } = this.props;
    const { currentStep, errors, newEmailAddress } = this.state;
    const lastErrorArr = errors.length > 0 ? [errors[0]] : [];

    switch (currentStep) {
      case FIRST_STEP:
        return (
          <StepOne
            errors={lastErrorArr}
            handleTextInputAddress={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
      case SECOND_STEP:
        return (
          <StepTwo
            currentEmail={currentEmail}
            errors={lastErrorArr}
            handleTextInputAddress={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
      case THIRD_STEP:
        return (
          <StepThree
            emailAddress={newEmailAddress.value}
            errors={lastErrorArr}
            handleTextInputAddress={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
      case FOURTH_STEP:
        return <StepFour />;
      default:
        return (
          <StepOne
            errors={lastErrorArr}
            handleTextInputAddress={this.handleTextInputAddress}
            handleTextInputBlur={this.handleTextInputBlur}
          />
        );
    }
  };

  renderModal = () => {
    const { apiCallState, t } = this.props;

    const {
      modalOpen,
      currentStep,
      newEmailAddress,
      verificationCode
    } = this.state;

    const buttonProps = {
      label:
        currentStep === FOURTH_STEP ? t('actions:finish') : t('actions:next'),
      onClick: currentStep === FOURTH_STEP ? this.handleOkButton : this.nextStep
    };

    if (currentStep === FIRST_STEP && !newEmailAddress.valid)
      buttonProps.disabled = 'disabled';
    if (currentStep === SECOND_STEP && !verificationCode.valid)
      buttonProps.disabled = 'disabled';

    return (
      <Modal
        open={modalOpen}
        onClose={this.handleCloseModalClick}
        classNames={{
          modal: 'info-modal change-process',
          overlay: 'info-overlay',
          closeButton: `info-modal__close-btn${
            apiCallState === 'start' ? '--hidden' : ''
          }`
        }}
      >
        <svg className="blur-svg">
          <defs>
            <filter id="blur-filter">
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>
        </svg>
        <div
          className={apiCallState === 'start' ? 'info-modal--blurred' : null}
          onKeyPress={this.handleKeyPress}
          role="presentation"
        >
          <Svgicon
            icon={EmailIcon}
            alt="email_icon"
            customClass="sms-icon-change"
            themed
            active
          />

          {this.renderModalContent()}
          <Button {...buttonProps} />
        </div>
        {apiCallState === 'start' ? <AppLoader size="modal" /> : null}
      </Modal>
    );
  };

  render() {
    const { currentEmail, t, nonEditableFields } = this.props;
    return (
      <ChangeEmailProcessSC>
        <div className="input-wrapper">
          <div className="input-wrapper__left">
            <h6>{t('profile.email.label')}</h6>
          </div>
          <div className="input-wrapper__right">
            <TextInput
              label={t('profile.email.label')}
              customClass="txt-inp"
              defaultValue={currentEmail}
              disabled
            />
          </div>
        </div>
        <div className="button-wrapper">
          <Button
            styling="secondary"
            label={t('profile.email.changeAction')}
            onClick={this.handleChangeEmailBtnClick}
            disabled={nonEditableFields.includes('email')}
          />
        </div>
        {this.renderModal()}
      </ChangeEmailProcessSC>
    );
  }
}

ChangeEmailProcess.propTypes = {
  apiCallState: PropTypes.string.isRequired,
  currentEmail: PropTypes.string.isRequired,
  emailChangeProcess: PropTypes.shape({}).isRequired,
  changeEmail: PropTypes.func.isRequired,
  verifyEmail: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
  nonEditableFields: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changeEmail: ActionCreators.changeEmail,
      verifyEmail: ActionCreators.verifyEmail,
      updateUser: ActionCreators.updateUser
    },
    dispatch
  );

export default withTranslation('pages')(
  connect(
    state => ({
      apiCallState: state.ui.emailChangeProcess.apiCallState,
      currentEmail: state.user.email,
      emailChangeProcess: state.ui.emailChangeProcess,
      user: state.user,
      nonEditableFields: state.user.nonEditableFields
    }),
    mapDispatchToProps
  )(withTheme(ChangeEmailProcess))
);
